/**
 * O texto de um produto reduzido à forma que se compara.
 *
 * `DET YPE 500ML`, `Detergente Ypê 500 ml` e `detergente ype 500ml` têm de
 * chegar ao mesmo lugar — é o que faz um item de nota fiscal encontrar o
 * `Product` que já existe, em vez de criar o vigésimo "detergente".
 *
 * ## Por que ela existe ANTES do matching
 *
 * A fase pede isto por escrito: *"função de normalização de texto já
 * implementada e **versionada**, mesmo antes do matching. É ela que a
 * [[Fase 8 - Nota fiscal e IA]] vai usar."* Escrevê-la junto do matching seria
 * escrevê-la sob pressão do primeiro caso que aparecesse.
 *
 * ## A VERSÃO é o ponto, e não é cerimônia
 *
 * Todo texto normalizado gravado no banco é o resultado **desta** versão da
 * função. No dia em que ela mudar — e ela vai mudar, porque abreviação de
 * mercado é um poço sem fundo —, os valores gravados passam a estar numa forma
 * que a função nova não produz mais. Sem a versão gravada ao lado, não há como
 * saber **quais** documentos precisam ser reprocessados: seria tudo ou nada.
 *
 * Com ela, o reprocessamento é uma consulta: `where('normalizedVersion', '<',
 * NORMALIZED_TEXT_VERSION)`.
 *
 * ## O que ela faz, em ordem, e por que cada passo
 *
 * 1. **minúsculas** — `DET` e `det` são a mesma palavra;
 * 2. **tira acento** — a nota fiscal do mercado quase nunca acentua, e `ypê` e
 *    `ype` têm de casar;
 * 3. **separa número de letra** — `500ml` vira `500 ml`, senão o tamanho gruda
 *    na unidade e nenhum dos dois é comparável;
 * 4. **troca `,` por `.` entre dígitos** — `1,5l` é um litro e meio, e `1.5` é
 *    o que o resto do sistema entende;
 * 5. **tudo que não é letra, dígito ou ponto vira espaço** — hífen, barra e
 *    parêntese não distinguem produto;
 * 6. **colapsa espaço**.
 *
 * **O que ela NÃO faz, de propósito:** não expande abreviação (`det` →
 * `detergente`), não tira palavra comum, não corrige digitação. Cada uma dessas
 * é um dicionário que envelhece e erra em silêncio — e a decisão de tê-las é da
 * Fase 8, com dado real de nota fiscal na mão. Esta função é a base de que todas
 * elas partiriam, e ela é reversível: sempre dá para normalizar mais tarde, e
 * nunca dá para recuperar o que foi jogado fora cedo.
 */

/**
 * **Suba isto sempre que mudar `normalizedText`.** É o que separa "já
 * normalizado" de "normalizado por uma versão que não existe mais", e é o que
 * permite reprocessar só o que precisa.
 *
 * Histórico:
 * - **1** (2026-09-01) — versão inicial: caixa, acento, número colado à
 *   unidade, vírgula decimal, pontuação e espaço.
 */
export const NORMALIZED_TEXT_VERSION = 1

export function normalizedText(raw: string): string {
  return (
    raw
      .normalize('NFD')
      // Tira os diacríticos. `NFD` separa a letra do acento, e esta classe
      // apaga o acento — sem tabela de "á→a" para alguém manter.
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      // Vírgula decimal entre dígitos vira ponto. Só entre dígitos: a vírgula
      // que separa palavras vira espaço no passo seguinte.
      .replace(/(\d),(\d)/g, '$1.$2')
      // Número colado em letra, nos dois sentidos: `500ml` e `ml500`.
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/([a-z])(\d)/g, '$1 $2')
      // O resto da pontuação some. O ponto sobrevive porque é decimal — e um
      // ponto solto vira espaço no colapso seguinte.
      .replace(/[^a-z0-9.]+/g, ' ')
      // Ponto que não está entre dígitos não é decimal: é fim de abreviação.
      .replace(/\.(?!\d)/g, ' ')
      .replace(/(?<!\d)\./g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
  )
}
